import React, { useState, useEffect } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { CompetitorAdvantage, CompetitorCategory } from '../types/competitor';

interface AdvantageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (advantage: Partial<CompetitorAdvantage>) => void;
  advantage?: CompetitorAdvantage | null;
  title?: string;
}

const AdvantageModal: React.FC<AdvantageModalProps> = ({
  isOpen,
  onClose,
  onSave,
  advantage,
  title = 'Add Advantage'
}) => {
  const [formData, setFormData] = useState<Partial<CompetitorAdvantage>>({
    point: '',
    category: CompetitorCategory.OTHER,
    evidence: ''
  });

  useEffect(() => {
    if (advantage) {
      setFormData({
        point: advantage.point || '',
        category: advantage.category || CompetitorCategory.OTHER,
        evidence: advantage.evidence || ''
      });
    } else {
      setFormData({
        point: '',
        category: CompetitorCategory.OTHER,
        evidence: ''
      });
    }
  }, [advantage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.point?.trim()) {
      onSave(formData);
      onClose();
    }
  };

  const getCategoryLabel = (category: CompetitorCategory): string => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Advantage Point *
              </label>
              <textarea
                value={formData.point}
                onChange={(e) => setFormData({ ...formData, point: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                required
                placeholder="Describe the advantage..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as CompetitorCategory })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {Object.values(CompetitorCategory).map((cat) => (
                  <option key={cat} value={cat}>
                    {getCategoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Evidence (Optional)
              </label>
              <textarea
                value={formData.evidence}
                onChange={(e) => setFormData({ ...formData, evidence: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Supporting evidence or data..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              disabled={!formData.point?.trim()}
            >
              {advantage ? 'Update' : 'Add'} Advantage
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvantageModal;